/**
 * reportPaging — leitura PAGINADA para os relatórios.
 *
 * ⚠️ Armadilha #50, o modo de falha mais perigoso de um relatório: um `select`
 * sem `.range()` devolve no máximo **1.000 linhas** e o PostgREST NÃO avisa que
 * cortou. O período sai truncado, os totais ficam menores que a realidade, e não
 * há erro, log ou toast — o número só está errado. Foi o que já aconteceu com o
 * `.limit(500)` do histórico de estoque.
 *
 * Toda leitura nova que alimenta agregação passa por aqui. Quando a paginação
 * bate no teto de páginas, `truncated` volta `true` e a TELA MOSTRA o aviso
 * ("escolha um período menor") — corte silencioso é proibido (no silent caps).
 *
 * O único sinal confiável de fim é uma página INCOMPLETA. Parar quando `data`
 * vem vazio custaria uma requisição a mais; parar por contagem exigiria um
 * `count` que o PostgREST só dá com header próprio.
 */

/** Tamanho de página. 1.000 é o teto padrão do PostgREST. */
export const REPORT_PAGE_SIZE = 1000;

/** Teto de segurança: 50 páginas = 50 mil linhas por período. */
export const REPORT_MAX_PAGES = 50;

export interface PagedRows<T> {
  rows: T[];
  /** `true` = bateu no teto de páginas; os números estão INCOMPLETOS. */
  truncated: boolean;
}

/**
 * Lê todas as páginas de uma consulta. `runPage` recebe os índices inclusivos
 * que vão para `.range(from, to)`.
 */
export async function fetchAllPages<T>(
  runPage: (from: number, to: number) => Promise<{ data: T[] | null; error: unknown }>,
  pageSize: number = REPORT_PAGE_SIZE,
  maxPages: number = REPORT_MAX_PAGES,
): Promise<PagedRows<T>> {
  const rows: T[] = [];

  for (let page = 0; page < maxPages; page += 1) {
    const start = page * pageSize;
    const { data, error } = await runPage(start, start + pageSize - 1);
    if (error) throw error;
    const batch = data ?? [];
    rows.push(...batch);
    // Página incompleta = acabou.
    if (batch.length < pageSize) return { rows, truncated: false };
  }

  return { rows, truncated: true };
}
